<template>
  <div>
    <label
      class="check-box"
      :class="{
        'check-box--checked': modelValue,
        'check-box--disabled': disabled,
        'check-box--error': !!error,
        [`check-box--${type}`]: true,
        [`check-box--${size}`]: true,
      }"
    >
      <input
        :id="id"
        type="checkbox"
        class="check-box__input"
        :checked="modelValue"
        :value="modelValue"
        :name="name"
        :disabled="disabled"
        @input="onCheck"
      />
      <div class="check-box__control">
        <Icon
          name="mono-check"
          class="check-box__control-icon"
        />
      </div>
      <div
        v-if="$slots['label']"
        class="check-box__label"
      >
        <slot name="label" />
      </div>
    </label>
    <div
      v-if="error && typeof error === 'string'"
      class="check-box__error"
    >
      {{ error }}
    </div>
  </div>
</template>

<script lang="ts">
export default {
  name: 'CheckBox',
}
</script>

<script setup lang="ts">
import type { DarwinSizeType } from '@/uiKit/types'
import Icon from '@/uiKit/Icon/Icon.vue'

withDefaults(
  defineProps<{
    name: string
    modelValue: boolean
    id?: string
    disabled?: boolean
    type?: 'circle' | 'rect'
    size: DarwinSizeType
    error?: string
  }>(),
  {
    name: '',
    id: undefined,
    modelValue: undefined,
    disabled: false,
    type: 'rect',
    error: undefined,
  },
)
const emit = defineEmits<{
  (event: 'update:modelValue', value: boolean): void
}>()

const onCheck = (e: Event): void => {
  const checked = (e.target as HTMLInputElement).checked
  emit('update:modelValue', checked)
}
</script>

<style lang="scss" scoped>
@import '@/uiKit/assets/index';
.check-box {
  display: grid;
  grid-auto-flow: column;
  column-gap: 0.5em;
  justify-content: start;
  align-items: center;
  height: 16px;
  transition: color 0.2s;
  cursor: pointer;
  color: var(--color-text-subtle);

  &--disabled {
    cursor: auto;
  }

  &__control {
    @include row--center;
    height: 16px;
    width: 16px;
    color: $colorWhite;
    transition:
      color,
      background-color 0.2s;

    &-icon {
      color: transparent;
      font-weight: bold;
      transition: opacity 0.2s;
      opacity: 0;
    }
  }
}

.check-box:not(.check-box--disabled) .check-box__control,
.check-box:not(.check-box--disabled) .check-box__input {
  cursor: pointer;
}

.check-box--checked {
  .check-box__control {
    background: $colorCheckBoxDefault;

    &-icon {
      opacity: 1;
      color: $colorWhite;
    }
  }
}

.check-box__control {
  border: 1.5px solid #dee0e3;
}

.check-box--checked .check-box__control {
  border-color: $colorCheckBoxDefault;
}

.check-box__input,
.check-box__control {
  border-radius: 4px;

  grid-column: 1 / 2;
  grid-row: 1 / 2;
}

.check-box__input {
  opacity: 0;
}

.check-box__input,
.check-box__control {
  height: 15px;
  width: 15px;
}

.check-box__control {
  transition: all 0.2s;
  transition-property: background-color;
}

.check-box:hover:not(.check-box--disabled):not(.check-box--checked) .check-box__control {
  background: var(--color-surface-tertiary);
  border-color: var(--color-border-input-hovered);
}
.check-box:active:not(.check-box--disabled) .check-box__control {
  background: $colorCheckBoxActive;
}
.check-box:focus:not(.check-box--disabled) .check-box__control {
  box-shadow: 0px 0px 0px 1.5px $colorCheckBoxFocus;
}

.check-box--error .check-box__control {
  background: $colorStatusNegativeSurface;
  border-color: $colorInteractiveNegativeDefault;
}

.check-box__label {
  @include fontRegularBody200;
  color: var(--color-text-default);
}

.check-box--disabled {
  opacity: 0.5;
}

.check-box__error {
  margin-top: 4px;
  @include typographyRegularBody100;
  color: $colorStatusNegative;
}

.check-box.check-box--lg {
  height: 20px;

  .check-box__label {
    @include typographyRegularBody200;
  }
  .check-box__control {
    height: 20px;
    width: 20px;
  }
  .check-box__input,
  .check-box__control {
    border-radius: 6px;
  }
}

.check-box--circle .check-box__control {
  border-radius: 50%;

  &-icon {
    transform-origin: center;
    transform: scale(0.8);
  }
}
</style>
